// src/contexts/ClientsContext.tsx
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';
import { useEvents } from './EventsContext';
import {
    fetchClientsService,
    createClientService,
    updateClientService,
    deleteClientService,
    getDeletionStrategy,
    createCustomerReportService,
    fetchCustomerReportsService,
    deleteCustomerReportsService,
    mergeCustomersService,
} from '@/services/clients/clientService';

const ClientsContext = createContext<any | null>(null);

export const useClients = () => {
    const context = useContext(ClientsContext);
    if (!context) {
        throw new Error('useClients must be used within a ClientsProvider');
    }
    return context;
};

export const ClientsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [blackList, setBlackList] = useState<string[]>([]);
    const [clients, setClients] = useState<any[]>([]);
    const [customerReports, setCustomerReports] = useState<any[]>([]);
    const { organization, isAuthenticated } = useAuth();
    const { bookings } = useEvents();

    useEffect(() => {
        if (isAuthenticated && organization?.id && bookings) {
            fetchClients();
            fetchBlackList();
            fetchAllCustomerReports();
        }
    }, [isAuthenticated, organization?.id, bookings]);

    const fetchClients = async () => {
        try {
            const data = await fetchClientsService(bookings || [], organization!.id);
            setClients(data);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchBlackList = async () => {
        const { data, error } = await supabase
            .from('BlackList')
            .select('client_id')
            .eq('shop_id', organization!.id);

        if (error) {
            console.error('Errore nel recupero della blacklist:', error);
            return;
        }

        setBlackList(data.map((item: any) => item.client_id));
    };

    const fetchAllCustomerReports = async () => {
        try {
            const result = await fetchCustomerReportsService(organization!.id);
            if (result.success && result.data) {
                setCustomerReports(result.data);
            }
        } catch (error) {
            console.error('Errore nel recupero di tutte le segnalazioni:', error);
        }
    };

    const getClientById = (id: string) =>
        clients.find((client) => client.id === id);

    const blockClient = async (id: string) => {
        try {
            const { error } = await supabase.from('BlackList').insert({
                client_id: id,
                shop_id: organization!.id,
            });

            // 23505 is the code for unique constraint violation
            if (error && error.code !== '23505') {
                throw new Error('Errore nel blocco del cliente');
            }

            setBlackList((prev) => prev.includes(id) ? prev : [...prev, id]);
            return { success: true, error: '' };
        } catch (e: any) {
            console.error('Errore nel blocco del cliente:', e);
            return { success: false, error: e.message };
        }
    };

    const unlockClient = async (id: string) => {
        try {
            const { error } = await supabase
                .from('BlackList')
                .delete()
                .eq('client_id', id)
                .eq('shop_id', organization!.id);

            if (error) {
                throw new Error("Errore nello sblocco del cliente");
            }

            setBlackList((prev) => prev.filter((clientId) => clientId !== id));
            return { success: true, error: '' };
        } catch (e: any) {
            console.error("Errore nell'sblocco del cliente:", e);
            return { success: false, error: e.message };
        }
    };

    const createClient = async (clientData: any) => {
        const result = await createClientService(clientData, organization!.id);
        if (result.success && result.data) {
            setClients((prev) => [...prev, result.data]);
        }
        // Il cliente creato torna al chiamante: serve a chi lo crea al volo mentre
        // prenota, per selezionarlo subito senza doverlo ricercare di nuovo.
        return { success: result.success, error: result.error, data: result.data };
    };

    const updateClient = async (id: string, clientData: any) => {
        const result = await updateClientService(id, clientData, organization!.id);
        if (result.success && result.data) {
            setClients((prev) =>
                prev.map((g) => (g.id === id ? result.data : g)),
            );
        }
        return { success: result.success, error: result.error };
    };

    const deleteClient = async (id: string) => {
        const strategy = getDeletionStrategy(id, bookings || []);

        const result = await deleteClientService(id, strategy === 'SOFT_DELETE');

        if (result.success) {
            if (result.isSoftDelete && result.data) {
                setClients((prev) =>
                    prev.map((g) => (g.id === id ? result.data : g)),
                );
            } else {
                setClients((prev) => prev.filter((g) => g.id !== id));
            }
        }
        return { success: result.success, error: result.error };
    };

    const createCustomerReport = async (reportData: any) => {
        const result = await createCustomerReportService({ ...reportData, shop_id: organization!.id });
        if (result.success && result.data) {
            setCustomerReports((prev) => [result.data, ...prev]);
        }
        return result;
    };

    const resetCustomerReports = async (clientId: string) => {
        const result = await deleteCustomerReportsService(organization!.id, clientId);
        if (result.success) {
            setCustomerReports((prev) => prev.filter((r) => r.client_id !== clientId));
        }
        return result;
    };

    const mergeClients = async (customerIds: string[]) => {
        const result = await mergeCustomersService(customerIds);
        if (result.success) {
            await fetchClients(); // Ricarica la lista per avere il nuovo stato dei fusi
        }
        return result;
    };

    const value = {
        clients,
        blackList,
        customerReports,
        fetchBlackList,
        getClientById,
        blockClient,
        unlockClient,
        createClient,
        updateClient,
        deleteClient,
        createCustomerReport,
        resetCustomerReports,
        mergeClients,
    };

    return (
        <ClientsContext.Provider value={value}>
            {children}
        </ClientsContext.Provider>
    );
};